import { useState } from "react";
import { Mail, Loader2, Send, UserRound } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_EQUIPE_E_PARCEIROS } from "@/lib/ajuda/dialogos/equipe-e-parceiros";
import { env } from "@/lib/env";
import { ResultStage, DialogField, formatExpiry } from "@/components/shared/InviteResultStage";

type Props = {
  open: boolean;
  onClose: () => void;
};

type Stage =
  | { kind: "form" }
  | { kind: "result"; token: string; email: string | null; expiresAt: Date | null; multiClinic: boolean };

/**
 * Convite de parceria do laboratório a um dentista — o caminho único.
 *
 * Email é opcional de propósito: com ele, a plataforma envia o convite por email e, se a pessoa já
 * tiver conta, o convite também aparece dentro do app; sem ele, sai só o link, pra mandar por
 * WhatsApp. Nos dois casos é o MESMO convite — o link da tela de resultado é o mesmo que foi por
 * email, e quem se cadastrar por ele sai do onboarding já conectado.
 *
 * A resposta é idêntica exista ou não conta com o email informado — nada aqui denuncia se o
 * endereço é de um usuário da plataforma.
 */
export function ConvidarParceiroDialog({ open, onClose }: Props) {
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [multiClinic, setMultiClinic] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const create = trpc.partner.connectInvite.create.useMutation({
    onSuccess: (invite) => {
      setStage({
        kind: "result",
        token: invite.token,
        email: invite.email ?? null,
        expiresAt: invite.expiresAt ? new Date(invite.expiresAt) : null,
        multiClinic: invite.multiClinic ?? false,
      });
    },
    onError: (err) => toast.error(err.message || "Erro ao gerar convite."),
  });

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStage({ kind: "form" });
      setEmail("");
      setNome("");
      setMultiClinic(false);
      setCopied(false);
      setShowQr(false);
    }, 250);
  };

  const inviteUrl = stage.kind === "result" ? `${env.landingUrl}/conectar-dentista/${stage.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const whatsappText = `Olá! Vamos trabalhar juntos no DentalBook? Aceite a parceria aqui: ${inviteUrl}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate({
      email: email.trim() || undefined,
      dentistDisplayName: nome.trim() || undefined,
      expiresInDays: 14,
      multiClinic,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle
            className="dark:text-foreground"
            ajuda={
              stage.kind === "form"
                ? AJUDA_EQUIPE_E_PARCEIROS.convidarParceiro
                : AJUDA_EQUIPE_E_PARCEIROS.conviteDeParceriaCriado
            }
          >
            {stage.kind === "form" ? "Convidar parceiro" : "Convite criado"}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {stage.kind === "form"
              ? "Informe o email pra plataforma enviar o convite — ou gere só o link e mande por onde preferir. Quem aceita sai conectado ao laboratório."
              : stage.email
                ? `O email já foi enviado. O link abaixo é o mesmo convite — use como reforço no WhatsApp.${stage.multiClinic ? " O dentista poderá conectar várias clínicas dele." : ""}`
                : stage.multiClinic
                  ? "Compartilhe o link com o dentista — ele poderá conectar várias clínicas dele num aceite só."
                  : "Compartilhe o link com o dentista — ao aceitar, ele escolhe qual clínica conectar."}
          </DialogDescription>
        </DialogHeader>

        {stage.kind === "form" && (
          <form onSubmit={handleSubmit} className="space-y-3">
            <DialogField icon={Mail} label="Email do dentista (opcional)">
              <input
                type="email"
                name="bd-partner-invite-email"
                autoComplete="off"
                data-1p-ignore
                data-lpignore
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="dentista@exemplo.com"
                className="w-full bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 sm:text-sm dark:text-foreground dark:placeholder:text-muted-foreground"
              />
            </DialogField>

            <label className="flex min-h-[44px] cursor-pointer items-start gap-2.5 rounded-lg border border-zinc-200 px-3 py-2.5 sm:min-h-0 dark:border-border">
              <input
                type="checkbox"
                checked={multiClinic}
                onChange={(e) => setMultiClinic(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
              />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-zinc-800 dark:text-foreground">
                  Permitir conectar várias clínicas
                </span>
                <span className="block text-[12px] leading-snug text-zinc-500 dark:text-muted-foreground">
                  Um dentista com mais de uma clínica poderá conectar todas (ou escolher quais) num
                  aceite só. Desligado, o convite conecta uma clínica.
                </span>
              </span>
            </label>

            <DialogField icon={UserRound} label="Nome pra exibição (opcional)">
              <input
                type="text"
                name="bd-partner-invite-nome"
                autoComplete="off"
                data-1p-ignore
                data-lpignore
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Dra. Fulana"
                className="w-full bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 sm:text-sm dark:text-foreground dark:placeholder:text-muted-foreground"
              />
            </DialogField>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={create.isPending}
                className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
              >
                {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {email.trim() ? "Enviar convite" : "Gerar link"}
              </button>
            </div>
          </form>
        )}

        {stage.kind === "result" && (
          <ResultStage
            inviteUrl={inviteUrl}
            phone={null}
            email={stage.email}
            whatsappHref={whatsappHref}
            mailtoHref=""
            copied={copied}
            onCopy={copy}
            showQr={showQr}
            onToggleQr={() => setShowQr((v) => !v)}
            expiryLabel={stage.expiresAt ? formatExpiry(stage.expiresAt) : null}
            onNewLink={() => setStage({ kind: "form" })}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
